import { useEffect, useRef, useState } from 'react';

import { DropDownOption } from '@/caregiverSpace/shared/model';

interface UseSelectSearchProps<TOption> {
    selectedOption?: DropDownOption<TOption> | null;
    onSearchChange?: (searchValue: string) => void;
    hasServerSideSearch?: boolean;
    options?: DropDownOption<TOption>[];
}

export const useSelectSearch = <TOption>({
    selectedOption,
    onSearchChange,
    hasServerSideSearch = false,
    options = [],
}: UseSelectSearchProps<TOption>) => {
    const [searchTerm, setSearchTerm] = useState(
        selectedOption?.displayValue ?? '',
    );
    const [isFocused, setIsFocused] = useState(false);
    const currentSearchRef = useRef<string>('');
    const debounceTimeoutRef = useRef<NodeJS.Timeout>();

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        currentSearchRef.current = value;

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        if (onSearchChange) {
            debounceTimeoutRef.current = setTimeout(() => {
                if (currentSearchRef.current === value) {
                    onSearchChange(value);
                }
            }, 500);
        }
    };

    const filteredOptions = hasServerSideSearch
        ? options
        : options.filter((option) =>
              option.displayValue
                  .toLowerCase()
                  .includes(searchTerm.trim().toLowerCase()),
          );

    useEffect(() => {
        if (selectedOption && !isFocused) {
            setSearchTerm(selectedOption.displayValue);
            currentSearchRef.current = selectedOption.displayValue;
        }
    }, [selectedOption, isFocused]);

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    return {
        searchTerm,
        setSearchTerm,
        isFocused,
        setIsFocused,
        filteredOptions,
        handleSearchChange,
        currentSearchRef,
        debounceTimeoutRef,
    };
};
